// src/components/extraction/export.js
// Baut aus der aktuellen Rechnungsliste den Export-Payload (rohe Werte + Markierungen, KEINE
// bereits formatierten Anzeige-Strings - das Backend soll echte Excel-Zahlen/-Daten erzeugen,
// keine Text-Zellen) und ruft den Export-Endpoint auf.
import api from '../../api';
import { baueZeilenAnsicht } from './rechnungAnsicht';
import getMwstSpalten from './mwstSpalten';

/**
 * Baut den JSON-Payload für den Export. Enthält bewusst KEINE der bereits als String
 * formatierten wertFormatiert-Felder aus baueZeilenAnsicht - das Backend erzeugt daraus
 * eigene, echte Excel-Zahlen-/Datums-Zellen.
 *
 * "dateiname" kommt aus eintrag.file (siehe useDokumentListe.jsx) und existiert nur im Export
 * (nicht in der Web-Tabelle) - bei Erfassung über Kamera/Mehrbild-Aufnahme ist file null,
 * dann bleibt das Feld leer.
 */
export function baueExportPayload(eintraege) {
  return {
    steuersaetze: getMwstSpalten().map((spalte) => spalte.key), // z.B. ["0", "7", "19"]
    zeilen: eintraege.map((eintrag) => {
      const ansicht = baueZeilenAnsicht(eintrag);
      return {
        lieferant: ansicht.lieferant.wert,
        lieferantKlasse: ansicht.lieferant.klasse ?? null,
        lieferantAdresse: ansicht.lieferantAdresse.wert,
        lieferantAdresseKlasse: ansicht.lieferantAdresse.klasse ?? null,
        rechnungsnummer: ansicht.rechnungsnummer.wert,
        rechnungsnummerKlasse: ansicht.rechnungsnummer.klasse ?? null,
        rechnungsdatum: ansicht.rechnungsdatum.wert, // roh: "yyyy-MM-dd" oder null
        rechnungsdatumKlasse: ansicht.rechnungsdatum.klasse ?? null,
        brutto: ansicht.brutto.wert,
        bruttoKlasse: ansicht.brutto.klasse ?? null,
        netto: ansicht.netto.wert,
        nettoKlasse: ansicht.netto.klasse ?? null,
        skonto: ansicht.skonto.wert,
        skontoKlasse: ansicht.skonto.klasse ?? null,
        umsatzsteuerId: ansicht.umsatzsteuerId.wert,
        umsatzsteuerIdKlasse: ansicht.umsatzsteuerId.klasse ?? null,
        iban: ansicht.iban.wert,
        ibanKlasse: ansicht.iban.klasse ?? null,
        mwst: ansicht.mwst.map((spalte) => ({
          satz: spalte.satz,
          wert: spalte.wert,
          klasse: spalte.klasse ?? null,
        })),
        status: ansicht.status.text,
        statusKlasse: ansicht.status.klasse,
        positionencheck: ansicht.positionencheck.text,
        kommentar: ansicht.kommentar,
        dateiname: eintrag.file?.name ?? '',
      };
    }),
  };
}

/**
 * Fordert die Export-Datei vom Backend an und löst den Download im Browser aus.
 */
export async function exportiereRechnungen(eintraege, token) {
  const payload = baueExportPayload(eintraege);

  const response = await api.post('/export/rechnungen', payload, {
    responseType: 'blob',
    headers: { Authorization: `Bearer ${token}` },
  });

  const zeitstempel = new Date().toISOString().slice(0, 10);
  const dateiname = `Rechnungen_${zeitstempel}.xlsx`;

  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = dateiname;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Wie exportiereRechnungen, aber statt Download wird dieselbe Excel-Datei serverseitig per
 * E-Mail an die übergebene Adresse verschickt (POST /export/rechnungen/email, siehe
 * ExportEndpoints.cs / DatenLeserApi.SendeDateiPerEmailAsync). Wirft bei Fehlschlag (ungültige
 * Adresse, SMTP-Fehler) - der Aufrufer (EmailExportDialog) fängt das und zeigt die Meldung an.
 */
export async function sendeRechnungenPerEmail(eintraege, empfaengerAdresse, token) {
  const payload = {
    export: baueExportPayload(eintraege),
    empfaengerAdresse,
  };

  await api.post('/export/rechnungen/email', payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
}